import React from "react";
import {
  AttributeExistingContract,
  CONTRACT_PHASE,
  getAttributeExistingContract,
} from "screens/sites/components/buildingAttributeStatus";

/** Dutch label per contract phase, kept short so it fits in a badge. */
const PHASE_LABEL: Record<string, string> = {
  [CONTRACT_PHASE.ACTIVE]: "Lopend contract",
  [CONTRACT_PHASE.TERMINATION]: "In opzeg",
  [CONTRACT_PHASE.CONCEPT]: "Conceptcontract",
};

// Status colour only on the dot; the text stays dark for contrast on light backgrounds.
const DOT_CLASS: Record<AttributeExistingContract["status"], string> = {
  rented: "bg-status-good",
  termination: "bg-status-medium",
};

interface AttributeExistingContractBadgeProps {
  /** Any building attribute carrying `contractProducts` (as returned by the building-attributes API). */
  attribute: Parameters<typeof getAttributeExistingContract>[0];
  /** Compact: only the phase (for dense grids). Default also shows tenant(s) and unit. */
  compact?: boolean;
  className?: string;
}

/**
 * Shows the contract an attribute (garage, berging, ...) is currently tied to, with its phase.
 * Renders nothing when the attribute is vacant, so it can be dropped into any attribute list.
 */
export const AttributeExistingContractBadge: React.FC<AttributeExistingContractBadgeProps> = ({
  attribute,
  compact = false,
  className = "",
}) => {
  const existing = getAttributeExistingContract(attribute);
  if (!existing) return null;

  const phaseLabel = PHASE_LABEL[existing.phase] || existing.phase;
  const details: string[] = [];
  if (!compact) {
    if (existing.tenantNames.length > 0) {
      details.push(
        `${existing.tenantNames.length > 1 ? "Huurders" : "Huurder"}: ${existing.tenantNames.join(", ")}`,
      );
    }
    details.push(existing.unitId ? `Unit ${existing.unitId}` : "Los gebouwattribuut");
  }

  const title = [phaseLabel, ...details, existing.contractId ? `Contract #${existing.contractId}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <span
      data-testid="attribute-existing-contract-badge"
      data-status={existing.status}
      title={title}
      className={`inline-flex max-w-full items-center gap-1.5 rounded-md border border-neutral-primary/15 bg-white px-2 py-0.5 text-xs text-neutral-primary ${className}`}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASS[existing.status]}`} aria-hidden="true" />
      <span className="font-medium">{phaseLabel}</span>
      {details.length > 0 && (
        <span className="truncate text-neutral-secondary-mid">· {details.join(" · ")}</span>
      )}
    </span>
  );
};

export default AttributeExistingContractBadge;
